import { usePricesHooks } from '../../../modules/operations'
import { useProductLoaders } from '../../../modules/products'
import { useCustomerTypeActions, useCustomerTypeBuilders, ProductYield, CustomerType, useCustomerTypeLoaders} from '../../../modules/sales'
import { isDefined } from '../../../utils/typeUtils'
import { FieldType } from '../../ui'
import { CsvImportResult, CsvRow } from '../types'
import { FILE_ROW_KEY, useImportCsv } from './importCsv'
import { CUSTOMER_TYPE_PRICE_LABEL, ImportCustomerTypeField } from './types'
import { useSystemStateHooks } from '../../../modules/system'

export const useImportCustomerType = () => {
    const importCsv = useImportCsv();
    const pricesHooks = usePricesHooks();
    const { buildCustomerType } = useCustomerTypeBuilders();
    const productLoaders = useProductLoaders();
    const customerTypeLoaders = useCustomerTypeLoaders();
    const { companyId } = useSystemStateHooks();

    const importData = (
        priceFieldValue: ImportCustomerTypeField,
        baseCustomerTypeId: string | undefined,
        existingCustomerTypeId: string | undefined
    ) => async (rows: CsvRow[]): Promise<CsvImportResult> => {

        const isVat = priceFieldValue === ImportCustomerTypeField.PRICE_VAT;
        const roundYield = (value: number) => Math.round(value * 10000) / 10000;
        const listGeneralYield = roundYield(Number(rows[0]?.["metadata_yield"] || 0));

        // Solo se procesan las filas con precio: el resto de la lista queda como está
        const pricedRows = rows.filter(row => isDefined(row["price"]));

        const { loadSupplierCodesProducts } = productLoaders();
        const allSupplierCodes = pricedRows.map(r => r["supplierCode"]).filter(c => isDefined(c)) as string[];
        const products = await loadSupplierCodesProducts(allSupplierCodes);

        const { loadCustomerType, findCustomerType } = customerTypeLoaders();

        const baseCustomerType = baseCustomerTypeId
            ? await loadCustomerType(baseCustomerTypeId)
            : null;
        const existingCustomerType = findCustomerType(existingCustomerTypeId);

        // Se parte de los ajustes actuales de la lista; si es nueva, de los excluidos de la base (igual que el form)
        const initialYields = existingCustomerType
            ? existingCustomerType.productYields || []
            : (baseCustomerType?.productYields || []).filter(py => py.isExcluded).map(py => ({ ...py, yield: 0 }));
        const productYieldsMap = new Map<string, ProductYield>(initialYields.map(py => [py.productId, py]));

        const importedRows: any[] = [];
        const unimportedRows: any[] = [];

        for (let i = 0; i < pricedRows.length; i++) {
            const row = pricedRows[i];
            const sCode = row["supplierCode"] as string;
            const inputPrice = row["price"] as number;
            // Los productos archivados no forman parte de las listas de precios
            const product = products.find(p => p.supplierCode === sCode && !p.archived);

            if (!product || productYieldsMap.get(product.id)?.isExcluded) {
                unimportedRows.push({ rowIndex: row[FILE_ROW_KEY], productName: product?.name || sCode || 'Desconocido' });
                continue;
            }

            // Mismo cálculo que el template y el form de la lista
            const basePrice = pricesHooks.product.adjustedPrice(product.id, isVat, baseCustomerTypeId);

            if (!isDefined(basePrice) || basePrice <= 0) {
                unimportedRows.push({ rowIndex: row[FILE_ROW_KEY], productName: product.name });
                continue;
            }

            const calculatedYield = pricesHooks.adjustment.calculateYield(inputPrice, basePrice);
            const finalYield = roundYield(calculatedYield);

            productYieldsMap.set(product.id, { productId: product.id, yield: finalYield, isExcluded: false });

            importedRows.push({
                rowIndex: row[FILE_ROW_KEY],
                productName: product.name,
                yield: finalYield,
                price: !isVat ? inputPrice : pricesHooks.product.removeVat(inputPrice, pricesHooks.product.vatTaxId(product.id)),
                priceVat: isVat ? inputPrice : pricesHooks.product.applyVat(inputPrice, pricesHooks.product.vatTaxId(product.id)),
                supplierCode: sCode
            });
        }

        // Los ajustes iguales al general no se guardan como particulares
        const productYields = Array.from(productYieldsMap.values())
            .filter(py => py.isExcluded || Math.abs(py.yield - listGeneralYield) >= 0.0001);

        const customerType = buildCustomerType({
            name: rows[0]?.["metadata_listName"] || "Lista Importada",
            yield: listGeneralYield,
            baseCustomerTypeId,
            productYields
        });

        return { importedRows, unimportedRows, customerType } as any;
    }

    return (file: File, priceFieldValue: ImportCustomerTypeField, onPersist: (ct: CustomerType) => Promise<CustomerType>, baseCustomerTypeId?: string, existingCustomerTypeId?: string) => {
        const priceLabel = CUSTOMER_TYPE_PRICE_LABEL[priceFieldValue];
        const columns = {
            "supplierCode": { name: "Código Interno", type: FieldType.TEXT },
            "price": { name: `Nuevo Precio ${priceLabel}`, type: FieldType.NUMBER }
        };

        return importCsv(file, columns, importData(priceFieldValue, baseCustomerTypeId, existingCustomerTypeId));
    }
}